import { simpleHash } from '@shared/hash';

export type QuickTemplateVariables = Record<string, unknown>;

export interface QuickTemplateInfo {
  readonly template: string;
  readonly fallbackText: string;
  readonly variables: QuickTemplateVariables;
}

export type QuickTranslateFn<TValue> = (key: string, info: QuickTemplateInfo) => TValue | null | undefined;

export interface CreateTqOptions<TValue extends string = string> {
  readonly translate: QuickTranslateFn<TValue>;
  readonly namespace?: string;
  readonly hashFn?: (input: string) => string;
  readonly onMissing?: (details: { key: string; info: QuickTemplateInfo }) => void;
  // If true (default), treat translator echoing the key as missing and use fallback
  readonly treatReturnedKeyAsMissing?: boolean;
  readonly debug?: (details: { stage: 'plural'; key: string; hashInput: string; info: QuickTemplateInfo }) => void;
  // Locale for plural rules (e.g., 'en', 'pl', 'ar'); defaults to host environment
  readonly locale?: string;
  // Optional resolver to determine locale at call time (e.g., to follow i18next language switches)
  readonly resolveLocale?: () => string | undefined;
}

export type QuickTaggedTemplate<TValue = string> = (values: TemplateStringsArray, ...args: ReadonlyArray<unknown>) => TValue;

export type QuickPluralForms = Record<string, string>;

export interface QuickPluralOptions {
  readonly variables?: QuickTemplateVariables;
}

export interface QuickTaggedTemplateWithPlural<TValue = string> extends QuickTaggedTemplate<TValue> {
  plural: (count: number, forms: QuickPluralForms, options?: QuickPluralOptions) => TValue;
}

const DEFAULT_NAMESPACE = 'quick';
const MAX_SNAPSHOT_HISTORY = 128;
const VALID_PLURAL_FORMS = new Set(['zero', 'one', 'two', 'few', 'many', 'other']);

interface TemplateSnapshot {
  readonly key: string;
  readonly info: QuickTemplateInfo;
  readonly output: string;
}

const buildTemplate = (values: TemplateStringsArray, args: ReadonlyArray<unknown>): QuickTemplateInfo => {
  let template = '';
  let fallbackText = '';
  const variables: QuickTemplateVariables = {};

  for (let index = 0; index < values.length; index += 1) {
    const chunk = values[index];
    template += chunk;
    fallbackText += chunk;

    if (index < args.length) {
      template += `{{${index}}}`;
      fallbackText += String(args[index] ?? '');
      variables[String(index)] = args[index];
    }
  }

  return { template, fallbackText, variables };
};

export const createTq = <TValue extends string = string>(options: CreateTqOptions<TValue>): QuickTaggedTemplateWithPlural<TValue> => {
  const namespace = options.namespace ?? DEFAULT_NAMESPACE;
  const hashFn = options.hashFn ?? simpleHash;
  const treatReturnedKeyAsMissing = options.treatReturnedKeyAsMissing ?? true;
  const onMissing = options.onMissing ?? (() => {});
  const debug = options.debug ?? (() => {});
  const resolveLocale = options.resolveLocale ?? (() => options.locale);

  const pluralRulesCache = new Map<string | undefined, Intl.PluralRules>();
  const getPluralRules = (locale: string | undefined): Intl.PluralRules => {
    const cacheKey = locale;
    const cached = pluralRulesCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    let rules: Intl.PluralRules;
    try {
      rules = locale ? new Intl.PluralRules(locale) : new Intl.PluralRules();
    } catch {
      // Fall back to environment locale if supplied locale is invalid
      rules = new Intl.PluralRules();
    }

    pluralRulesCache.set(cacheKey, rules);
    return rules;
  };

  // Mutation is intentional here: we keep a bounded ring buffer of recent
  // snapshots to enable plural back-referencing. Readonly type cannot express
  // this efficient push/shift behavior without refactoring.
  const snapshots: TemplateSnapshot[] = [];

  const storeSnapshot = (snapshot: TemplateSnapshot): void => {
    snapshots.push(snapshot);
    if (snapshots.length > MAX_SNAPSHOT_HISTORY) {
      snapshots.shift();
    }
  };

  const takeSnapshotForOutput = (output: unknown): TemplateSnapshot | undefined => {
    if (typeof output !== 'string') {
      return undefined;
    }

    // Search from newest to oldest so duplicate outputs reuse the latest template instance.
    for (let index = snapshots.length - 1; index >= 0; index -= 1) {
      const entry = snapshots[index];
      if (entry.output === output) {
        snapshots.splice(index, 1);
        return entry;
      }
    }

    return undefined;
  };

  const invokeTranslate = (key: string, info: QuickTemplateInfo): TValue => {
    const translated = options.translate(key, info);
    if (translated == null || (treatReturnedKeyAsMissing && translated === key)) {
      onMissing({ key, info });
      // Store snapshot so plural can recover the original template/variables
      storeSnapshot({ key, info, output: key });
      // Key is always a string; cast to TValue which is constrained to extend string
      return key as unknown as TValue;
    }

    // Snapshots are only stored for string outputs to enable plural back-references
    storeSnapshot({ key, info, output: translated });

    return translated as TValue;
  };

  const tq: QuickTaggedTemplateWithPlural<TValue> = (values, ...args) => {
    const info = buildTemplate(values, args);
    const hash = hashFn(info.template);
    const key = `${namespace}.${hash}`;
    return invokeTranslate(key, info);
  };

  const plural: QuickTaggedTemplateWithPlural<TValue>['plural'] = (count, forms, optionsOverride) => {
    const entries = Object.entries(forms);
    if (!entries.length) {
      throw new Error('tq.plural requires at least one plural form');
    }

    const canonicalForms = entries.map(([formKey, renderedValue]) => {
      if (!VALID_PLURAL_FORMS.has(formKey)) {
        throw new Error(
          `tq.plural form "${formKey}" is not a valid CLDR plural category. ` +
            `Valid forms are: ${Array.from(VALID_PLURAL_FORMS).join(', ')}`
        );
      }

      const snapshot = takeSnapshotForOutput(renderedValue);
      if (snapshot) {
        return {
          formKey,
          template: snapshot.info.template,
          fallbackText: renderedValue,
          variables: snapshot.info.variables,
        };
      }

      return {
        formKey,
        template: renderedValue,
        fallbackText: renderedValue,
        variables: {} as QuickTemplateVariables,
      };
    });

    // Sort by form key to ensure stable hash regardless of source order
    const sortedForms = [...canonicalForms].sort((a, b) => a.formKey.localeCompare(b.formKey));
    const hashInputParts = sortedForms.map(({ formKey, template }) => `${formKey}:${template}`);
    const hashInput = `plural|${hashInputParts.join('|')}`;
    const hash = hashFn(hashInput);
    const baseKey = `${namespace}.${hash}`;

    // Determine target category to align variables with what the translator will choose
    const effectiveLocale = resolveLocale();
    const targetCategory: string = getPluralRules(effectiveLocale).select(count);

    const preferredForm =
      canonicalForms.find(({ formKey }) => formKey === targetCategory) ??
      canonicalForms.find(({ formKey }) => formKey === 'other') ??
      canonicalForms[canonicalForms.length - 1];
    const fallbackText = forms[preferredForm.formKey];

    const info: QuickTemplateInfo = {
      template: preferredForm.template,
      fallbackText,
      variables: {
        ...preferredForm.variables,
        ...(optionsOverride?.variables ?? {}),
        count,
      },
    };

    const suffixedKey = `${baseKey}_${preferredForm.formKey}`;

    debug({ stage: 'plural', key: suffixedKey, hashInput, info });

    return invokeTranslate(suffixedKey, info);
  };

  tq.plural = plural;

  return tq;
};

// Matches {{0}}, {{1}}, etc. and replaces with corresponding indexed variable
export const interpolateQuickTemplate = (info: QuickTemplateInfo): string =>
  info.template.replace(/\{\{(\d+)}}/g, (_, index) => String((info.variables as Record<string, unknown>)[index] ?? ''));
